/** Tooltip primitive (Radix). */
import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

/** Radix tooltip provider for delay and skip-delay config. */
const TooltipProvider = TooltipPrimitive.Provider;

/** Radix tooltip root. */
const Tooltip = TooltipPrimitive.Root;

/** Element that shows the tooltip on hover or focus. */
const TooltipTrigger = TooltipPrimitive.Trigger;

/** Light / dark surface; `null` uses the popover tokens. */
type TooltipVariant = 'light' | 'dark' | null;

/** Props for {@link TooltipContent}. */
type TooltipContentProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
  variant?: TooltipVariant;
};

/** Surface classes for the light and dark tooltip variants. */
const variantClasses: Record<NonNullable<TooltipVariant>, string> = {
  light: 'bg-white text-gray-900 border-gray-200 shadow-md',
  dark: 'bg-gray-900 text-white border-gray-700 shadow-md',
};

/** Portaled tooltip body. */
const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 4, variant, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'radix-tooltip-content z-[9999] overflow-hidden rounded-md border px-3 py-1.5 text-sm shadow-md',
        variant ? variantClasses[variant] : 'bg-popover text-popover-foreground',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
